import React, { useEffect, useState } from 'react';
import {
  Box,
  MenuItem,
  Select,
  Stack,
  Grid,
  CardContent,
  Card,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import TitleWithAddButton from '../../Home/TitleWithAddButton';
import ReusableDrawer from '../../../Components/ReusableDrawer';
import StyledInputField from '../../../Components/Custom/StyledInputField';
import { align_column, VM_Colors } from '../../../Styles/VMStyles';
import MuiTypographyMandatory from '../../../Components/Custom/MuiTypographyMandatory';
import { API_ADD_PERMISSIONS, API_GET_ALL_PERMISSIONS } from '../../../Util/URLConstants';
import StorageFactory from '../../../Util/StorageFactory';
import { SESSION_USER_ID_KEY } from '../../../Util/VMConstants';
import MUITypography from '../../../Components/MUITypography';
import Loader from '../../../Components/Loader';

const comesUnderOptions = [
  'Enquiries',
  'Invoices',
  'Profile',
  'Roles & permission',
  'Appointments & FollowUps',
  'Tasks',
  'Checklists',
  'Digital Register',
  'FeedBack',
  'Event Photos',
  'Event Vendors',
  'Menu'
];

// initial form state
const initialPermissionData = {
  name: '',
  description: '',
  comesUnder: '',
  permission_id: '',
};

function Permissions() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permissionsList, setPermissionsList] = useState([]);

  const [permissionData, setPermissionData] = useState(initialPermissionData);
  const [errors, setErrors] = useState(initialPermissionData);

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  // Generate next permission ID
  const generateNextPermissionId = () => {
    if (permissionsList.length === 0) return 'VM001';

    const ids = permissionsList
      .map((p) => p.permission_id || p.id) // backend field might differ
      .filter((id) => /^VM\d+$/.test(id)); // ensure valid format

    if (ids.length === 0) return 'VM001';

    const maxNum = Math.max(...ids.map((id) => parseInt(id.replace('VM', ''), 10)));
    const nextNum = (maxNum + 1).toString().padStart(3, '0');
    return `VM${nextNum}`;
  };

  const addPermissionHandler = () => {
    const nextId = generateNextPermissionId();
    setPermissionData({ ...initialPermissionData, permission_id: nextId });
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setPermissionData(initialPermissionData);
    setErrors(initialPermissionData);
  };

  const validate = () => {
    const newErrors = {
      name: permissionData.name.trim() === '' ? 'Permission Name is required' : '',
      description: permissionData.description.trim() === '' ? 'Description is required' : '',
      comesUnder: permissionData.comesUnder === '' ? 'Please select a category' : '',
      permission_id: permissionData.permission_id.trim() === '' ? 'Permission ID is required' : '',
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => err === '');
  };

  const submitHandler = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const reqbody = {
        name: permissionData.name,
        description: permissionData.description,
        category: permissionData.comesUnder,
        permission_id: permissionData.permission_id,
        user_id: userID,
      };
      const response = await axios.post(API_ADD_PERMISSIONS, reqbody);
      const respData = response.data;
      if (response?.data?.status_code === 200) {
        toast.success('Permission created successfully');
        handleDrawerClose();
        fetchPermissions();
      } else {
        toast.error(respData?.message || 'Unexpected error');
      }
    } catch (error) {
      console.error('Error creating permission:', error);
      toast.error('Failed to create permission');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setPermissionData((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const fetchPermissions = async () => {
    try {
      const res = await axios.get(API_GET_ALL_PERMISSIONS);
      setPermissionsList(res.data.permissions || []);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  // ✅ Group permissions by category
  const groupedPermissions = permissionsList.reduce((acc, perm) => {
    const category = perm.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(perm);
    return acc;
  }, {});

  return (
    <Box>
      <TitleWithAddButton
        title="Permissions"
        onAddClick={addPermissionHandler}
        buttonLabel="Create Permission"
      />

      <Box sx={{ mt: 2 }}>
        {Object.entries(groupedPermissions).map(([category, perms]) => (
          <Box key={category} sx={{ mb: 4 }}>
            <MUITypography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              {category}
            </MUITypography>
            <Grid container spacing={2}>
              {perms.map((perm) => (
                <Grid item xs={12} sm={6} md={4} key={perm.permission_id || perm.id}>
                  <Card variant="outlined" sx={{ borderRadius: 2, p: 1 }}>
                    <CardContent sx={{ p: 1 }}>
                      <Box sx={{ ...align_column, minWidth: 300 }}>
                        <MUITypography fontSize={14} fontWeight={600}>
                          {perm.permission_id} - {perm.name}
                        </MUITypography>
                        <MUITypography fontSize={12} color="text.secondary">
                          {perm.description || ''}
                        </MUITypography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>

      <ReusableDrawer
        open={drawerOpen}
        title={'Create Permission'}
        onClose={handleDrawerClose}
        onSubmit={submitHandler}
        showFooter={true}
        loading={loading}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Permission ID (auto-generated, read-only) */}
            <StyledInputField
              heading="Permission ID"
              value={permissionData.permission_id}
              fullWidth
              required
              disabled
              error={Boolean(errors.permission_id)}
              helperText={errors.permission_id}
            />

            <StyledInputField
              heading="Permission Name"
              value={permissionData.name}
              onChange={handleChange('name')}
              fullWidth
              required
              error={Boolean(errors.name)}
              helperText={errors.name}
            />

            <StyledInputField
              heading="Permission Description"
              value={permissionData.description}
              onChange={handleChange('description')}
              fullWidth
              required
              multiline
              rows={3}
              error={Boolean(errors.description)}
              helperText={errors.description}
            />

            <Box sx={align_column}>
              <MuiTypographyMandatory label="Category" required />
              <Select
                name="comesUnder"
                value={permissionData.comesUnder}
                onChange={handleChange('comesUnder')}
                error={Boolean(errors.comesUnder)}
                sx={{ backgroundColor: VM_Colors.white }}
                displayEmpty
              >
                <MenuItem value="" disabled>  -- Select --   </MenuItem>
                {comesUnderOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {errors.comesUnder && (
                <MUITypography fontSize={12} color="error">
                  {errors.comesUnder}
                </MUITypography>
              )}
            </Box>
          </Stack>
        </Box>
      </ReusableDrawer>

      {
        loading &&
         <Loader loading={loading} /> 
      }
    </Box>
  );
}

export default Permissions;
